class UserKeys {
  constructor() {
    this.tableName = "user";
    this.id = "u";
    this.displayName = "n";
    this.readOnly = "ro";
    this.schemaVersion = "sv";
    this.paidProfiles = "pp";
    this.freeProfiles = "fp";
    this.email = "e";
    this.address = "a";

    this.decompress = this.decompress.bind(this);
  }

  decompress(doc) {
    const data = doc.data();
    if (!data) {
      return {id: doc.id};
    }
    const readOnlyData = data ? data[this.readOnly] : undefined;

    if (!readOnlyData) {
      console.error(`The profile returned (${doc.id}) is invalid`);
      return {id: doc.id};
    }

    return {
      id: doc.id,
      displayName: data[this.displayName],
      paidProfiles: readOnlyData[this.paidProfiles],
      freeProfiles: readOnlyData[this.freeProfiles],
      schemaVersion: readOnlyData[this.schemaVersion],
      email: data[this.email],
      address: data[this.address],
    };
  }
}

class ProfileKeys {
  constructor() {
    this.tableName = "profile";
    this.id = "id";
    this.displayName = "ds";
    this.genderIcons = "g";
    this.raceIcons = "r";
    this.owner = "o";
    this.sessionRunners = "rs";
    this.incidents = "i";
    this.certificatesOfAchievement = "coa";
    this.consentObtained = "c";
    this.version = "v";
    this.compress = this.compress.bind(this);
    this.decompress = this.decompress.bind(this);
  }

  _validateData(property) {
    if (property == undefined || property == null) {
      return false;
    }

    return true;
  }

  compress(data, owner) {
    return {
      [this.version]: data.version,
      [this.displayName]: data.displayName,
      [this.genderIcons]: data.genderIcons,
      [this.raceIcons]: data.raceIcons,
      [this.incidents]: data.incidents,
      [this.certificatesOfAchievement]: data.certificatesOfAchievement,
      [this.consentObtained]: data.consentObtained,
      [this.owner]: owner,
    };
  }

  compressChanges(data) {
    const compressedProfile = {
      [this.id]: data.id,
      [this.version]: data.version,
    };

    if (this._validateData(data.displayName)) compressedProfile[this.displayName] = data.displayName;
    if (this._validateData(data.genderIcons)) compressedProfile[this.genderIcons] = data.genderIcons;
    if (this._validateData(data.raceIcons)) compressedProfile[this.raceIcons] = data.raceIcons;
    if (this._validateData(data.incidents)) compressedProfile[this.incidents] = data.incidents;
    if (this._validateData(data.certificatesOfAchievement)) compressedProfile[this.certificatesOfAchievement] = data.certificatesOfAchievement;
    if (this._validateData(data.consentObtained)) compressedProfile[this.consentObtained] = data.consentObtained;

    return compressedProfile;
  }

  decompress(doc) {
    const data = doc.data();
    // This throws rather than returning an empty object, unlike Profile.decompress
    // on the functions side. There the empty object is the safe value: its only
    // consumer compares .owner, and an absent owner denies the request. Here
    // there is no safe empty value — consumers read .incidents.length and render
    // lists, so a partial object either crashes further from the cause or
    // renders a profile that does not exist.
    if (!data) {
      throw new Error(`ProfileKeys.decompress: profile ${doc.id} does not exist`);
    }

    return {
      id: doc.id,
      version: data[this.version],
      displayName: data[this.displayName],
      genderIcons: data[this.genderIcons],
      raceIcons: data[this.raceIcons],
      // Default to empty rather than undefined: consumers read .length on
      // these directly, and a document written without the field takes the
      // whole profile list down with it.
      incidents: data[this.incidents] || [],
      certificatesOfAchievement: data[this.certificatesOfAchievement] || [],
      consentObtained: data[this.consentObtained],
      owner: data[this.owner],
    };
  }
}

class ReportKeys {
  constructor() {
    this.decompress = this.decompress.bind(this);
  }

  decompress(doc) {
    const data = doc.data();
    // As with ProfileKeys.decompress: consumers read .questions and .date, so
    // there is no safe empty value to return here either.
    if (!data) {
      throw new Error(`ReportKeys.decompress: report ${doc.id} does not exist`);
    }

    return {
      id: doc.id,
      date: new Date(parseInt(doc.id)),
      questions: data.questions,
      notes: data.notes || "",
    };
  }
}

class ReportTypes {
  constructor() {
    this.Incident = "incident";
    this.CertificateOfAchievement = "coa";
  }

  profileKey(type) {
    const profileKeys = new ProfileKeys();
    if (type == this.Incident) return profileKeys.incidents;
    if (type == this.CertificateOfAchievement) return profileKeys.certificatesOfAchievement;
    return null;
  }
}

module.exports = {
  UserKeys: new UserKeys(),
  ProfileKeys: new ProfileKeys(),
  ReportKeys: new ReportKeys(),
  ReportTypes: new ReportTypes(),
};
